import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { ArrowLeft, Eye, EyeOff, Lock, CheckCircle, AlertTriangle } from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800 flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
          <AlertTriangle className="h-14 w-14 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-3">{t('invalidResetLink', 'Invalid Reset Link')}</h1>
          <p className="text-gray-600 text-sm mb-6">{t('invalidResetLinkDesc', 'This password reset link is invalid or has expired.')}</p>
          <Link to="/forgot-password" className="text-purple-600 hover:text-purple-700 font-semibold text-sm" data-testid="request-new-link">
            {t('requestNewLink', 'Request a new reset link')}
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('passwordMinLength', 'Password must be at least 6 characters'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('passwordsMustMatch', 'Passwords do not match'));
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post('/auth/reset-password', { token, newPassword: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('resetPasswordError', 'Failed to reset password. The link may have expired.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800">
      <header className="p-4 flex justify-between items-center">
        <button onClick={() => navigate('/signin')} className="flex items-center text-white hover:text-white/80" data-testid="back-button">
          <ArrowLeft className="h-6 w-6 mr-2" />
        </button>
        <LanguageToggle />
      </header>

      <div className="flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          {success ? (
            <div className="text-center py-4" data-testid="reset-password-success">
              <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-3">{t('passwordReset', 'Password Reset')}</h1>
              <p className="text-gray-600 mb-6 text-sm">
                {t('passwordResetSuccess', 'Your password has been reset successfully. You can now sign in with your new password.')}
              </p>
              <button
                onClick={() => navigate('/signin')}
                className="px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                data-testid="go-to-signin-btn"
              >
                {t('signIn')}
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <Lock className="h-10 w-10 text-purple-500 mb-3" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="reset-password-title">
                  {t('setNewPassword', 'Set a new password')}
                </h1>
                <p className="text-gray-600 text-sm">
                  {t('setNewPasswordDesc', 'Choose a strong password with at least 6 characters.')}
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('newPassword', 'New password')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('confirmPassword', 'Confirm password')}
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? t('resetting', 'Resetting...') : t('resetPassword', 'Reset Password')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
